// src/frontend/js/modern/shared/base-list-ui.js
// Single UI base shared by all list/counter modules (wishlist, waitlist, multi-list).
// Implements strict point updates:
//  - Existing DOM elements are NEVER rebuilt from templates.
//  - Templates are used ONLY to create brand-new nodes (optimistic/pending items,
//    or items that do not exist in the DOM yet during a diff-sync).
//  - Server responses patch individual fields, then the pending state is removed.
//
// Reactive rendering (Single Source of Truth):
//  - Every instance subscribes to stateCache for its own module.
//  - Any state mutation (optimistic, confirmed, rolled-back, synced) schedules
//    a single render pass via microtask batching.
//  - renderFromState() reconciles the entire counter/mini-list from state.
import { stateCache } from '../core/state-cache.js';

export class BaseListUI {
	constructor(config) {
		this.moduleName = config.moduleName;
		this.store = config.store;
		this.selectors = config.selectors || {};
		this._selectors = {
			table: `.wlfmc-${this.moduleName}-table`,
			tableWrapper: `.wlfmc-${this.moduleName}-table-wrapper`,
			row: '.wlfmc-table-item',
			...this.selectors
		};
		this._counterSelectors = this._resolveCounterSelectors();
		this._configCache = new Map();
		this._renderScheduled = false;
		// Reactive subscription: render automatically whenever the module's
		// state changes. Microtask batching inside StateCache guarantees one
		// render per tick regardless of how many mutations occurred.
		if (this.store) {
			this._stateUnsubscribe = stateCache.subscribe(this.moduleName, () => {
				this._scheduleRender();
			});
		} else {
			this._stateUnsubscribe = null;
		}
	}
	/* ═══════════════════════════════════════════
	   REACTIVE RENDERING (Single Source of Truth)
	════════════════════════════════════════════ */
	/**
	 * Schedule a render pass in the next microtask.
	 * Multiple state mutations within the same tick collapse into one render.
	 */
	_scheduleRender() {
		if (this._renderScheduled) return;
		this._renderScheduled = true;
		Promise.resolve().then(() => {
			this._renderScheduled = false;
			this.renderFromState();
		});
	}
	/**
	 * Full render from state — the single entry point for all UI reconciliation.
	 * Reads items and count exclusively from StateCache (the single source of
	 * truth) and reconciles counter numbers, icons, mini-list rows, and
	 * empty-state visibility.
	 *
	 * Subclasses with different rendering needs (e.g. multi-list rows)
	 * override this method.
	 */
	renderFromState() {
		const items = stateCache.getItems(this.moduleName);
		const count = stateCache.getCount(this.moduleName);
		this.syncCounterItems(items);
		this.updateCounter(count);
	}
	/**
	 * Tear down the reactive subscription to prevent memory leaks.
	 */
	destroy() {
		if (this._stateUnsubscribe) {
			this._stateUnsubscribe();
			this._stateUnsubscribe = null;
		}
		this._configCache.clear();
	}
	/* ═══════════════════════════════════════════
	   COUNTER SELECTORS / CONFIG
	   Unified wrapper selector with data-counter-type filtering.
	   PHP templates render 'wlfmc-counter-wrapper' on all counter types;
	   module-specific filtering is done via data-counter-type attribute.
	════════════════════════════════════════════ */
	_resolveCounterSelectors() {
		return {
			wrapper: '.wlfmc-counter-wrapper',
			itemsContainer: '.wlfmc-wishlist-items-wrapper',
			contentContainer: '.wlfmc-counter-content',
			viewLink: '.wlfmc-view-wishlist-link',
			total: '.total-products',
			totalCount: '.wlfmc-total-count',
			number: '.products-counter-number',
			icon: '.wlfmc-counter-icon',
			emptyClass: 'wlfmc-wishlist-empty'
		};
	}
	_getConfig(wrapper) {
		if (!wrapper) return null;
		const uniqueId = wrapper.dataset.id || wrapper.getAttribute('data-id');
		const counterType = wrapper.dataset.counterType || wrapper.getAttribute('data-counter-type') || this.moduleName;
		if (!uniqueId) {
			console.log('[BaseListUI] Counter wrapper missing data-id:', wrapper);
			return null;
		}
		const key = `${counterType}_${uniqueId}`;
		if (!this._configCache.has(key)) {
			this._configCache.set(key, {
				uniqueId: uniqueId,
				counterType: counterType,
				emptyIcon: wrapper.dataset.emptyIcon || '',
				hasItemsIcon: wrapper.dataset.hasItemsIcon || '',
				url: wrapper.dataset.wishlistUrl || wrapper.dataset.listsUrl || '',
				buttonText: wrapper.dataset.buttonText || '',
				totalText: wrapper.dataset.totalText || '',
				buttonPosition: wrapper.dataset.buttonPosition || 'after',
				showTotals: wrapper.dataset.showTotals !== '0',
				showButton: wrapper.dataset.showButton !== '0',
				showCounter: wrapper.dataset.showCounter !== '0',
				numberPosition:
					wrapper.dataset.productsNumberPosition ||
					this._detectNumberPosition(wrapper.querySelector('.wlfmc-counter')),
				token: wrapper.dataset.wishlistToken || '',
				id: wrapper.dataset.wishlistId || '',
				perPage:
					parseInt(wrapper.dataset.perPage || wrapper.dataset.per_page || '0', 10) || 0,
				hideZero: wrapper.dataset.hideZero === '1',
				hideIfEmpty: wrapper.dataset.hideIfEmpty === '1'
			});
		}
		return this._configCache.get(key);
	}
	_detectNumberPosition(anchor) {
		if (!anchor) return '';
		const cls = Array.from(anchor.classList).find((c) => c.startsWith('products-number-position-'));
		return cls ? cls.replace('products-number-position-', '') : '';
	}
	_ensureCounterNumbers(wrapper, config) {
		const selectors = this._counterSelectors;
		const anchor = wrapper.querySelector('.wlfmc-counter');
		if (!anchor || !config.showCounter) return;
		const pos = config.numberPosition || this._detectNumberPosition(anchor);
		const isRtl = document.documentElement.dir === 'rtl' || window.wlfmc_l10n?.is_rtl === true;
		const makeNumber = (extraClass) => {
			let num = null;
			if (extraClass) {
				num = wrapper.querySelector(`.products-counter-number.${extraClass}`);
			} else {
				num = anchor.querySelector(
					':scope > .products-counter-number:not(.position-top-left):not(.position-top-right)'
				);
			}
			if (!num) {
				num = document.createElement('span');
				num.className = 'wlfmc-counter-number products-counter-number' + (extraClass ? ' ' + extraClass : '');
			}
			return num;
		};
		if ('top-right' === pos || 'top-left' === pos) {
			const iconEl = wrapper.querySelector(selectors.icon);
			if (!iconEl) return;
			const num = makeNumber('top-right' === pos ? 'position-top-right' : 'position-top-left');
			if (num.parentNode !== iconEl) {
				iconEl.appendChild(num);
			}
			return;
		}
		const wantBeforeText = ('left' === pos && !isRtl) || ('right' === pos && isRtl);
		const num = makeNumber('');
		const textEl = anchor.querySelector(':scope > .wlfmc-counter-text');
		const iconEl = anchor.querySelector(':scope > .wlfmc-counter-icon');
		if (wantBeforeText) {
			if (textEl) {
				textEl.before(num);
			} else if (iconEl) {
				iconEl.after(num);
			} else {
				anchor.prepend(num);
			}
		} else if (textEl) {
			textEl.after(num);
		} else {
			anchor.appendChild(num);
		}
	}
	_forEachCounter(fn) {
		const moduleType = this.moduleName;
		document.querySelectorAll(this._counterSelectors.wrapper).forEach((wrapper) => {
			const counterType = wrapper.dataset.counterType || wrapper.getAttribute('data-counter-type') || '';
			if (counterType && counterType !== moduleType) return;
			const config = this._getConfig(wrapper);
			if (config) {
				fn(wrapper, config, this._counterSelectors);
			}
		});
	}

	/* ═══════════════════════════════════════════
	   COUNTER NUMBER / ICON / VISIBILITY
	   Single source of truth for all counter updates.
	   No external registry needed.
	════════════════════════════════════════════ */
	updateCounter(count = null) {
		let finalCount;
		if (count !== null && count !== undefined && Number.isFinite(count)) {
			finalCount = count;
		} else {
			try {
				finalCount = this.store.count.get();
			} catch (e) {
				finalCount = this.getDomCount ? this.getDomCount() : 0;
			}
		}
		const selectors = this._counterSelectors;
		const str = String(finalCount);
		this._forEachCounter((wrapper, config) => {
			this._ensureCounterNumbers(wrapper, config);
			wrapper.querySelectorAll(selectors.number).forEach((el) => {
				if (el.textContent !== str) el.textContent = str;
			});
			const iconEl = wrapper.querySelector(selectors.icon);
			if (iconEl) {
				this._updateIconGraphic(iconEl, finalCount > 0 ? config.hasItemsIcon : config.emptyIcon);
			}
			if (config.hideZero) {
				wrapper.querySelectorAll(selectors.number).forEach((el) => {
					el.style.display = finalCount === 0 ? 'none' : '';
				});
			}
			if (config.hideIfEmpty) wrapper.style.display = finalCount === 0 ? 'none' : '';
			wrapper.classList.toggle('empty', finalCount === 0);
			wrapper.querySelectorAll(`${selectors.total} ${selectors.totalCount}`).forEach((el) => {
				if (el.textContent !== str) el.textContent = str;
			});
			const itemsWrap = wrapper.querySelector(selectors.itemsContainer);
			const rowCount = itemsWrap ? itemsWrap.querySelectorAll('[data-row-id]').length : 0;
			const showStructure = finalCount > 0 && rowCount > 0;
			this._ensureExtras(wrapper, config, showStructure ? finalCount : 0);
			this._setEmptyState(wrapper, config, !showStructure);
		});
	}
	_updateIconGraphic(iconEl, html) {
		const previous = iconEl.dataset.wlfmcIcon || '';
		if (previous === html) return;
		iconEl.dataset.wlfmcIcon = html;
		const holder = document.createElement('div');
		holder.innerHTML = html;
		const nextNodes = Array.from(holder.childNodes);
		Array.from(iconEl.childNodes).forEach((node) => {
			if (node.nodeType === 1 && node.classList && node.classList.contains('products-counter-number')) {
				return;
			}
			node.remove();
		});
		const firstNumber = iconEl.querySelector('.products-counter-number');
		nextNodes.forEach((node) => {
			iconEl.insertBefore(node, firstNumber || null);
		});
	}
	/* ═══════════════════════════════════════════
	   TOTALS & VIEW LINKS (empty <-> full structure switch)
	════════════════════════════════════════════ */
	_ensureExtras(wrapper, config, count) {
		const selectors = this._counterSelectors;
		const content = wrapper.querySelector(selectors.contentContainer);
		if (!content) return;
		const itemsWrap = content.querySelector(selectors.itemsContainer);
		const totals = content.querySelector(':scope > ' + selectors.total);
		let beforeLink = null;
		let afterLink = null;
		content.querySelectorAll(':scope > ' + selectors.viewLink).forEach((link) => {
			if (link.classList.contains('center-align')) {
				afterLink = afterLink || link;
			} else {
				beforeLink = beforeLink || link;
			}
		});
		if (count > 0) {
			if (!beforeLink && config.showButton && 'before' === config.buttonPosition && config.buttonText) {
				beforeLink = document.createElement('a');
				beforeLink.href = config.url;
				beforeLink.className = selectors.viewLink.replace('.', '');
				beforeLink.textContent = config.buttonText;
				content.insertBefore(beforeLink, content.firstChild);
			}
			if (!totals && config.showTotals && itemsWrap) {
				const totalsEl = document.createElement('div');
				totalsEl.className = 'total-products d-flex space-between';
				const title = document.createElement('span');
				title.className = 'wlfmc-total-title';
				title.textContent = config.totalText;
				const value = document.createElement('span');
				value.className = 'wlfmc-total-count';
				value.textContent = String(count);
				totalsEl.appendChild(title);
				totalsEl.appendChild(value);
				itemsWrap.after(totalsEl);
			}
			if (!afterLink && config.showButton && 'after' === config.buttonPosition && config.buttonText) {
				afterLink = document.createElement('a');
				afterLink.href = config.url;
				afterLink.className = selectors.viewLink.replace('.', '') + ' center-align';
				afterLink.textContent = config.buttonText;
				content.appendChild(afterLink);
			}
			content.querySelectorAll(`${selectors.total}, ${selectors.viewLink}`).forEach((el) => {
				el.style.display = '';
			});
		} else {
			content.querySelectorAll(`${selectors.itemsContainer},${selectors.total}, ${selectors.viewLink}`).forEach((el) => {
				el.remove();
			});
		}
	}
	_setEmptyState(wrapper, config, isEmpty) {
		const selectors = this._counterSelectors;
		const content = wrapper.querySelector(selectors.contentContainer);
		if (!content) return;
		const existingEmpty = content.querySelector(':scope > .' + selectors.emptyClass);
		if (isEmpty && !existingEmpty) {
			const template = window.wp?.template?.(`wlfmc-mini-${config.counterType}-empty-${config.uniqueId}`);
			if (template) {
				const holder = document.createElement('div');
				holder.innerHTML = template({});
				const emptyEl = holder.firstElementChild;
				if (emptyEl) content.appendChild(emptyEl);
			}
		} else if (!isEmpty && existingEmpty) {
			existingEmpty.remove();
		}
		this._ensureExtras(wrapper, config, isEmpty ? 0 : 1);
	}
	_ensureItemsWrapper(wrapper) {
		const selectors = this._counterSelectors;
		const content = wrapper.querySelector(selectors.contentContainer);
		if (!content) return null;
		let itemsWrap = content.querySelector(':scope > ' + selectors.itemsContainer);
		if (!itemsWrap) {
			itemsWrap = document.createElement('div');
			itemsWrap.className = 'wlfmc-mini-wishlist-list wlfmc-wishlist-items-wrapper';
			const anchor =
				content.querySelector(':scope > ' + selectors.total) ||
				Array.from(content.children).find(
					(el) => el.classList.contains('center-align') && el.matches(selectors.viewLink)
				);
			content.insertBefore(itemsWrap, anchor || null);
		}
		return itemsWrap;
	}
	/* ═══════════════════════════════════════════
	   COUNTER ITEMS (point-update primitives)
	════════════════════════════════════════════ */
	_resolveDisplay(item) {
		if (!item) return null;
		const pid = String(item.product_id);
		const shared = this.store && this.store.getDisplayData ? this.store.getDisplayData(pid) : null;
		const listSpecific = {};
		if (item.item_id !== undefined) listSpecific.item_id = item.item_id;
		if (item.wishlist_id !== undefined) listSpecific.wishlist_id = item.wishlist_id;
		if (item.remove_url !== undefined) listSpecific.remove_url = item.remove_url;
		if (item.remove_nonce !== undefined) listSpecific.remove_nonce = item.remove_nonce;
		if (item.list_name !== undefined) listSpecific.list_name = item.list_name;
		if (item.wishlist_token !== undefined) listSpecific.wishlist_token = item.wishlist_token;
		if (shared) {
			return { ...shared, ...listSpecific };
		}
		if (item.display && (item.display.name || item.display.image)) {
			return { ...item.display, ...listSpecific };
		}
		if (item.name || item.image) return item;
		return null;
	}
	_renderItemElement(wrapper, config, productId, data, pending) {
		const template = window.wp?.template?.(`wlfmc-mini-${config.counterType}-item-${config.uniqueId}`);
		if (!template) {
			console.log('[BaseListUI] Template not found:', `wlfmc-mini-${config.counterType}-item-${config.uniqueId}`);
			return null;
		}
		const holder = document.createElement('div');
		holder.innerHTML = template({ product_id: productId, ...data, pending: !!pending });
		return holder.firstElementChild;
	}
	_patchItemElement(el, data = {}, pending = false) {
		if (!el) return;
		if (pending) {
			el.classList.add('wlfmc-pending');
		} else {
			if (data.image) {
				const thumb = el.querySelector('.product-thumbnail');
				if (thumb) thumb.innerHTML = data.image;
			}
			if (data.name !== undefined && data.name !== '') {
				const nameEl = el.querySelector('.product-name');
				if (nameEl) nameEl.textContent = data.name;
			}
			if (data.price) {
				const priceSpan = el.querySelector('.product-price span');
				if (priceSpan) priceSpan.innerHTML = data.price;
			}
			if (data.item_id) el.dataset.itemId = data.item_id;
			if (data.wishlist_id) el.dataset.wishlistId = data.wishlist_id;
			if (data.wishlist_token) el.dataset.wishlistToken = data.wishlist_token;
			if (data.url) {
				el.querySelectorAll('a.product-thumbnail, a.product-name').forEach((a) => {
					a.setAttribute('href', data.url);
				});
			}
			if (data.remove_url || data.remove_nonce) {
				const removeLink = el.querySelector('.remove_from_wishlist');
				if (removeLink) {
					if (data.remove_url) removeLink.setAttribute('href', data.remove_url);
					if (data.remove_nonce) removeLink.setAttribute('data-nonce', data.remove_nonce);
				}
			}
			if (data.list_name !== undefined) {
				const badgeHost = el.querySelector('.max-100');
				if (badgeHost) {
					let badge = badgeHost.querySelector('.wlfmc-badge');
					if (data.list_name) {
						if (!badge) {
							badge = document.createElement('div');
							badge.className = 'wlfmc-badge';
							badgeHost.appendChild(badge);
						}
						badge.textContent = data.list_name;
						badge.style.display = '';
					} else if (badge) {
						badge.remove();
					}
				}
			}
			el.classList.remove('wlfmc-pending');
		}
		el.querySelectorAll('a, button, input, select').forEach((child) => {
			if (pending) {
				child.setAttribute('disabled', 'disabled');
			} else {
				child.removeAttribute('disabled');
			}
			child.style.pointerEvents = '';
		});
	}
	addCounterItem(productId, itemData = {}) {
		this._forEachCounter((wrapper, config) => {
			const itemsWrap = this._ensureItemsWrapper(wrapper);
			if (!itemsWrap) return;
			if (itemsWrap.querySelector(`[data-row-id="${productId}"]`)) return;
			const itemEl = this._renderItemElement(wrapper, config, productId, itemData, true);
			if (!itemEl) return;
			itemsWrap.appendChild(itemEl);
			this._setEmptyState(wrapper, config, false);
		});
	}
	confirmCounterItem(productId, serverData = {}) {
		this._forEachCounter((wrapper) => {
			const itemsWrap = wrapper.querySelector(this._counterSelectors.itemsContainer);
			if (!itemsWrap) return;
			const itemEl = itemsWrap.querySelector(`[data-row-id="${productId}"]`);
			if (!itemEl) return;
			this._patchItemElement(itemEl, serverData, false);
		});
	}
	removeCounterItem(productId) {
		this._forEachCounter((wrapper, config) => {
			const itemsWrap = wrapper.querySelector(this._counterSelectors.itemsContainer);
			if (!itemsWrap) return;
			const itemEl = itemsWrap.querySelector(`[data-row-id="${productId}"]`);
			if (itemEl) itemEl.remove();
			const remaining = itemsWrap.querySelectorAll('[data-row-id]');
			if (remaining.length === 0) {
				this._setEmptyState(wrapper, config, true);
			}
		});
	}
	/**
	 * Diff-sync counter mini-list rows with the current StateCache snapshot.
	 *
	 * In merge mode (wishlist module only), uses getMergedWishlistItems()
	 * which returns deduplicated items from ALL lists with concatenated
	 * list_name badges. This ensures the counter reflects the true merged
	 * state without requiring a page refresh.
	 *
	 * Optimistic-aware reconciliation:
	 *  - Items with _optimistic: true  → rendered/kept as pending rows
	 *  - Items with _optimistic: false → confirmed rows (pending class removed)
	 *  - Pending DOM rows whose state item is now confirmed → patched & un-pended
	 *  - Pending DOM rows whose state item is still pending  → left untouched
	 *  - Stale rows not present in state → removed (pending or not)
	 */
	syncCounterItems(items = []) {
		const selectors = this._counterSelectors;
		const isMerge = this.moduleName === 'wishlist' &&
			stateCache.getConfig()?.mergeLists === true;

		// In merge mode, use the deduplicated merged item set so the counter
		// shows exactly one row per unique product across all lists.
		const source = isMerge
			? stateCache.getMergedWishlistItems()
			: (items || []).filter((item) => item && item.product_id !== undefined);

		this._forEachCounter((wrapper, config) => {
			const content = wrapper.querySelector(selectors.contentContainer);
			if (!content) return;
			const itemsWrap = this._ensureItemsWrapper(wrapper);
			const desired = new Map();
			const limited = config.perPage > 0 ? source.slice(0, config.perPage) : source;
			limited.forEach((item) => desired.set(String(item.product_id), item));
			desired.forEach((item, pid) => {
				let el = itemsWrap.querySelector(`[data-row-id="${pid}"]`);
				const isPending = item._optimistic === true;
				if (el) {
					if (el.classList.contains('wlfmc-pending')) {
						if (isPending) {
							// Still pending — only update the list_name badge
							// since list membership may have changed.
							if (isMerge && item.list_name !== undefined) {
								this._patchItemElement(el, { list_name: item.list_name }, true);
							}
							return;
						}
						// Was pending, now confirmed — patch fields and remove pending state
						const data = this._resolveDisplay(item);
						this._patchItemElement(el, data || {}, false);
						return;
					}
					// Existing confirmed row — patch if needed
					if (!isPending) {
						const data = this._resolveDisplay(item);
						if (data) this._patchItemElement(el, data, false);
					}
				} else {
					// Row does not exist yet — create it
					const data = this._resolveDisplay(item);
					if (!isPending && (!data || (!data.name && !data.image))) return;
					el = this._renderItemElement(wrapper, config, pid, data || {}, isPending);
					if (!el) return;
					if (isPending) el.classList.add('wlfmc-pending');
					itemsWrap.appendChild(el);
				}
			});
			// Remove stale rows that are no longer in state
			itemsWrap.querySelectorAll('[data-row-id]').forEach((el) => {
				const pid = el.getAttribute('data-row-id');
				if (!desired.has(String(pid))) {
					el.remove();
				}
			});
			const hasRows = itemsWrap.querySelectorAll('[data-row-id]').length > 0;
			this._setEmptyState(wrapper, config, !hasRows);
		});
	}
	/* ═══════════════════════════════════════════
	   UTILITIES
	════════════════════════════════════════════ */
	getDomCount() {
		const ids = new Set();
		document.querySelectorAll(`.wlfmc-add-to-${this.moduleName}.exists`).forEach((el) => {
			const cls = Array.from(el.classList).find((c) => c.startsWith(`wlfmc-add-to-${this.moduleName}-`));
			const pid = cls ? cls.replace(`wlfmc-add-to-${this.moduleName}-`, '') : (el.dataset.productId || '');
			if (pid) ids.add(String(pid));
		});
		return ids.size;
	}
	reInit() {
		this._configCache.clear();
	}
}
export default BaseListUI;
